import { css } from '@emotion/react';
import {
  useAudienceDocShareLinkMutators,
  useAudienceDocShareLinkStatus,
  useFeatureFlags,
} from '@kaiizen/kai-pro-api-react-state';
import { useState } from 'react';

import { ANALYTICS_EVENTS, trackEvent } from '../analytics';
import { useAuth, useKaiProApiClient, useNavigateToScan, useScanEditingContextSlice, useToast } from '../State';
import { LAYOUT2, TYPOGRAPHY2 } from '../Styles';
import { Button } from '../Widgets';
import { useAudienceDocShareLinkReplacementConfirmation } from './useAudienceDocShareLinkReplacementConfirmation';

const CONTAINER_STYLE = css`
  label: AUDIENCE_DOC_SHARE_ACTIONS;
  display: flex;
  align-items: center;
  gap: ${LAYOUT2.spacing1}px;
`;

const META_STYLE = css`
  label: AUDIENCE_DOC_SHARE_META;
  ${TYPOGRAPHY2.xSmall}
  color: var(--text2);
  max-width: 280px;
  text-align: right;
`;

const formatExpiresAt = (expiresAt: Date | string | undefined) =>
  (expiresAt ? new Date(expiresAt) : undefined)?.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

export const AudienceDocShareActions = () => {
  const { featureFlags } = useFeatureFlags();
  const { userCan } = useAuth();
  const apiClient = useKaiProApiClient();
  const navigateToScan = useNavigateToScan();
  const { showToast, showErrorToast } = useToast();
  const confirmAudienceDocShareLinkReplacement = useAudienceDocShareLinkReplacementConfirmation();
  const { scan, scanIsEditable } = useScanEditingContextSlice(['scan', 'scanIsEditable']);
  const [isCreatingAudienceDoc, setIsCreatingAudienceDoc] = useState(false);
  const featureEnabled = !!featureFlags.newScopeBuilderFlow;
  const canEditAudienceDocs = featureFlags.turnBasedHandoff
    ? scanIsEditable
    : userCan('update', 'PropertyProject');

  const canCreateAudienceDoc = !!scan
    && scan.scanType !== 'audienceDoc'
    && !scan.archivedAt
    && featureEnabled
    && userCan('create', 'Scan')
    && canEditAudienceDocs;

  const canManageAudienceDocShare = !!scan
    && scan.scanType === 'audienceDoc'
    && !!scan.parentScanId
    && !scan.archivedAt
    && featureEnabled
    && canEditAudienceDocs;

  const { shareLinkStatus, isLoading, refetch } = useAudienceDocShareLinkStatus(
    scan?.id,
    { enabled: canManageAudienceDocShare },
  );
  const {
    createAudienceDocShareLink,
    isCreatingAudienceDocShareLink,
    revokeAudienceDocShareLink,
    isRevokingAudienceDocShareLink,
  } = useAudienceDocShareLinkMutators();

  if (!scan || !featureEnabled) return null;

  const onCreateAudienceDoc = async () => {
    if (!scan || !canCreateAudienceDoc) return;

    setIsCreatingAudienceDoc(true);
    try {
      const audienceDoc = await apiClient.createScan({
        name: scan.name ? `Audience Doc - ${scan.name}` : 'Audience Doc',
        parentScanId: scan.id,
        scanType: 'audienceDoc',
      });
      trackEvent(ANALYTICS_EVENTS.audienceDocCreated, {
        audienceDocId: audienceDoc.id,
        parentScanId: scan.id,
        source: 'scope_action',
      });
      trackEvent(ANALYTICS_EVENTS.scanCreated, {
        scanId: audienceDoc.id,
        parentScanId: scan.id,
        scanType: 'audienceDoc',
      });
      showToast('Audience doc created.', 'success');
      navigateToScan({ scanId: audienceDoc.id });
    } catch (error) {
      showErrorToast(error, 'create an audience doc');
    } finally {
      setIsCreatingAudienceDoc(false);
    }
  };

  if (canCreateAudienceDoc) {
    return (
      <div css={CONTAINER_STYLE}>
        <Button
          buttonStyle='white'
          onClick={onCreateAudienceDoc}
          disabled={isCreatingAudienceDoc}
        >
          {isCreatingAudienceDoc ? 'Creating...' : 'Create audience doc'}
        </Button>
      </div>
    );
  }

  if (!canManageAudienceDocShare) return null;

  const hasActiveLink = !!shareLinkStatus?.hasActiveLink;

  const onCopyShareLink = async () => {
    if (!canManageAudienceDocShare) return;
    try {
      const { url } = await createAudienceDocShareLink(scan.id);
      await navigator.clipboard.writeText(url);
      await refetch();
      trackEvent(hasActiveLink ? ANALYTICS_EVENTS.audienceDocShareLinkReplaced : ANALYTICS_EVENTS.audienceDocShareLinkCreated, {
        audienceDocId: scan.id,
      });
      showToast(
        hasActiveLink
          ? 'Copied a new audience doc share link. The previous link is now inactive.'
          : 'Audience doc share link copied to clipboard.',
        'success',
      );
    } catch (error) {
      showErrorToast(error, 'copy an audience doc share link');
    }
  };

  const requestCopyShareLink = () =>
    hasActiveLink
      ? confirmAudienceDocShareLinkReplacement(onCopyShareLink)
      : onCopyShareLink();

  const onRevokeShareLink = async () => {
    if (!canManageAudienceDocShare) return;
    try {
      await revokeAudienceDocShareLink(scan.id);
      await refetch();
      trackEvent(ANALYTICS_EVENTS.audienceDocShareLinkRevoked, { audienceDocId: scan.id });
      showToast('Audience doc share link revoked.', 'success');
    } catch (error) {
      showErrorToast(error, 'revoke an audience doc share link');
    }
  };

  const statusMessage = isLoading
    ? 'Checking public share status...'
    : hasActiveLink
    ? `Public share active until ${formatExpiresAt(shareLinkStatus?.expiresAt)}`
    : 'Public sharing is off until you copy a secure link.';

  return (
    <div css={CONTAINER_STYLE}>
      <div css={META_STYLE}>{statusMessage}</div>
      <Button
        buttonStyle='white'
        onClick={requestCopyShareLink}
        disabled={isCreatingAudienceDocShareLink}
      >
        {isCreatingAudienceDocShareLink
          ? 'Copying...'
          : hasActiveLink
          ? 'Copy new link'
          : 'Copy share link'}
      </Button>
      {hasActiveLink && (
        <Button
          buttonStyle='white'
          onClick={onRevokeShareLink}
          disabled={isRevokingAudienceDocShareLink}
        >
          {isRevokingAudienceDocShareLink ? 'Revoking...' : 'Revoke link'}
        </Button>
      )}
    </div>
  );
};
